/**
 * @file Shared React component — components/common/TextInput.tsx.
 */

// src/components/common/TextInput.tsx
import React from "react";
import "./TextInput.css";
import FormFieldLabel from "./FormFieldLabel";

/** Type definition — Text Input Props. */
export interface TextInputProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;

  // ✅ Optional fields
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  type?: string; // 'text' | 'email' | 'number' etc.
  className?: string;
  infoText?: string | null; // Info text to display in tooltip
  error?: string; // Error message to display
  /** CMS textMaxLength / numeric bounds — forwarded to the native input. */
  maxLength?: number;
  min?: number | string;
  max?: number | string;
  step?: number | string;
}

const TextInput: React.FC<TextInputProps> = ({
  label,
  name,
  value,
  onChange,
  placeholder = "",
  required = false,
  disabled = false,
  type = "text",
  className = "",
  infoText,
  error,
  maxLength,
  min,
  max,
  step,
}) => {
  return (
    <div className={`form-group ${className}`}>
      <FormFieldLabel
        label={label}
        htmlFor={name}
        required={required}
        infoText={infoText}
      />

      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        maxLength={maxLength}
        min={min}
        max={max}
        step={step}
        className={`text-input ${disabled ? "disabled" : ""} ${error ? "error" : ""}`}
      />
      {error && <span className="error-message">{error}</span>}
    </div>
  );
};

/** React component — Text Input. */
export default TextInput;
